import { Absolute, Box, Flex } from 'mds-web-ui'
import { AnimatePresence } from 'motion/react'
import React, {
  createContext,
  Suspense,
  useContext,
  useImperativeHandle,
  useRef,
} from 'react'
import { lazyWithPreload, preloadOnIdle } from '../../utils/lazyWithPreload'
import { connect } from 'react-redux'
import { getBGColor } from '../../constants/actionsThemeColours'
import { buildActionHref, parseURL } from '../../utils/href'
import { navHeightPx } from '../TopNav'
import { TOOLTIP_MAIN } from '../utils/tooltipIds'
import { SlideLeftTransition } from '../utils/transitions'
import useCustomScrollbars, {
  scrollbarWidth,
} from '../utils/useCustomScrollbars'
import Schedule from '../Schedule'
import { width as actionCardWidth } from './ActionCard'
import { LogbookIcon, ScheduleIcon } from './icons'
import { IconLink } from './Link'

const Logbook = lazyWithPreload(
  () => import(/* webpackChunkName: "logbook" */ '../Logbook'),
)

// Warm on idle so the slide-left transition into the logbook starts immediately
// rather than after the chunk request.
preloadOnIdle(Logbook)

const LazyLogbook = (props) => (
  <Suspense fallback={<div />}>
    <Logbook {...props} />
  </Suspense>
)

export const scheduleWidth = actionCardWidth + 16 + 32 + 2 + scrollbarWidth * 2
// e.g. 382

const getURL = (location, toLogbook) => {
  const { actionId, actionSlug } = parseURL(location.pathname)
  if (actionId) {
    return buildActionHref(actionId, actionSlug, toLogbook)
  }

  return toLogbook ? '/logbook' : '/'
}

const ScheduleLink = ({
  location,
  logbookVisible,
  preferencesVisible,
  ...rest
}) => (
  <IconLink
    p="4px"
    to={getURL(location, false)}
    isActive={logbookVisible}
    icon={ScheduleIcon}
    aria-label="Show Schedule"
    data-tooltip-id={TOOLTIP_MAIN}
    data-tooltip-content="Show Schedule"
    data-tooltip-delay-show={300}
    {...rest}
  />
)
const LogbookLink = ({
  location,
  logbookVisible,
  preferencesVisible,
  ...rest
}) => (
  <IconLink
    p="4px"
    // visually centered within focus border
    // since SVG is slightly offset from centre
    pr="3px"
    to={getURL(location, true)}
    isActive={!logbookVisible}
    icon={LogbookIcon}
    aria-label="Show Logbook"
    data-tooltip-id={TOOLTIP_MAIN}
    data-tooltip-content="Show Logbook"
    data-tooltip-delay-show={300}
    {...rest}
  />
)

export const LogbookScheduleSwitcher = connect(({ location }) => ({
  location,
  logbookVisible: location.logbookVisible,
  preferencesVisible: location,
}))(({ darkMode, location, logbookVisible, preferencesVisible }) => (
  <Absolute top={0} right={0} zIndex={1}>
    <Flex mt={39} mr={34} role="navigation">
      <LogbookLink
        location={location}
        logbookVisible={logbookVisible}
        preferencesVisible={preferencesVisible}
        color={darkMode ? 'white' : 'black'}
      />
      <ScheduleLink
        location={location}
        logbookVisible={logbookVisible}
        preferencesVisible={preferencesVisible}
        color={darkMode ? 'white' : 'black'}
      />
    </Flex>
  </Absolute>
))

const Context = createContext()

export const useGetParentRef = () => {
  const ref = useContext(Context)
  return ref
}

export const LeftPaneWrapper = ({ logbookVisible = false, darkMode }) => {
  const localRef = useRef()
  const bg = getBGColor(darkMode)
  const paneKey = logbookVisible ? 'logbook' : 'schedule'

  const pane = logbookVisible ? (
    <LazyLogbook darkMode={darkMode} />
  ) : (
    <Schedule darkMode={darkMode} />
  )

  const animatedContent = (
    <AnimatePresence mode="wait" initial={false}>
      <SlideLeftTransition key={paneKey}>{pane}</SlideLeftTransition>
    </AnimatePresence>
  )

  const [ref, _scrollbarsShown, scrollbarStyles] = useCustomScrollbars(
    animatedContent,
    null,
    bg,
  )
  useImperativeHandle(localRef, () => ref.current)

  return (
    <Box
      width={scheduleWidth}
      pl={scrollbarWidth}
      pr={scrollbarWidth}
      css={{
        position: 'relative',
        flexShrink: 0,
        borderRight: darkMode ? '1px solid #262626' : '2px solid #eaeaea',
        height: `calc(100vh - ${navHeightPx})`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <LogbookScheduleSwitcher darkMode={darkMode} />
      <Context.Provider value={localRef}>
        <Box
          flex={1}
          css={{
            position: 'relative',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            ...scrollbarStyles,
          }}
          ref={ref}
        >
          {animatedContent}
        </Box>
      </Context.Provider>
    </Box>
  )
}
